import React, { useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useFeatureFlags } from './contexts/FeatureFlagsProvider';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import DashboardView from './pages/DashboardView';
import UsersView from './pages/UsersView';
import FollowUpsView from './pages/FollowUpsView';
import ProductsView from './pages/ProductsView';
import CategoriesView from './pages/CategoriesView';
import SubCategoriesView from './pages/SubCategoriesView';
import BrandsView from './pages/BrandsView';
import BusinessesView from './pages/BusinessesView';
import PlansView from './pages/PlansView';
import SubscribersView from './pages/SubscribersView';
import GeneralEnquiriesView from './pages/GeneralEnquiriesView';
import CustomPagesView from './pages/CustomPagesView';
import BlogsView from './pages/BlogsView';
import FestivalsView from './pages/FestivalsView';
import CustomerEnquiryDataView from './pages/CustomerEnquiryDataView';
import HelpRequestsView from './pages/HelpRequestsView';
import BuyerSellerChatView from './pages/BuyerSellerChatView';
import RfqView from './pages/RfqView';
import NewsPrView from './pages/NewsPrView';
import SignInView from './pages/SignInView';
import CountriesView from './pages/CountriesView';
import StatesView from './pages/StatesView';
import CitiesView from './pages/CitiesView';
import AutomationView from './pages/AutomationView';
import AutomationCenterView from './pages/AutomationCenterView';
import AnalyticsView from './pages/AnalyticsView';
import MarketingStepsView from './pages/MarketingStepsView';
import RevenueView from './pages/RevenueView';
import WbrReportView from './pages/WbrReportView';
import MessagesView from './pages/MessagesView';
import WhatsAppView from './pages/WhatsAppView';
import SettingsView from './pages/SettingsView';
import SopView from './pages/SopView';
import PartnersView from './pages/PartnersView';
import PartnerPayoutsView from './pages/PartnerPayoutsView';
import PartnerSettingsView from './pages/PartnerSettingsView';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import MobileBottomNav from './components/MobileBottomNav';
import ErrorBoundary from './components/ErrorBoundary';
import { logout } from './redux/slices/authSlice';


const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, token } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const location = useLocation();
  const ff = useFeatureFlags();

  useEffect(() => {
    const checkToken = () => {
      if (token) {
        try {
          const decodedJwt = JSON.parse(atob(token.split('.')[1]));
          if (decodedJwt && decodedJwt.exp * 1000 < Date.now()) {
            dispatch(logout());
            if (window.location.pathname !== '/signin') {
              window.location.replace('/signin');
            }
          }
        } catch (e) {
          dispatch(logout());
          if (window.location.pathname !== '/signin') {
            window.location.replace('/signin');
          }
        }
      }
    };
    // Re-check on mount/navigation, on window focus, and periodically — for
    // BOTH the signed-out and signed-in cases, so a logged-in admin whose token
    // expires mid-session is logged out proactively (not only on the next 401).
    checkToken();

    window.addEventListener('focus', checkToken);
    const interval = setInterval(checkToken, 60 * 1000);
    return () => {
      window.removeEventListener('focus', checkToken);
      clearInterval(interval);
    };
  }, [location, token, dispatch]);

  if (!isAuthenticated) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  // While flags load, hold the page so a disabled module can never flash.
  if (ff?.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', width: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Module disabled in System settings → block the route.
  if (ff && !ff.isPathEnabled(location.pathname)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const App = () => {
  const location = useLocation();
  const isSignInPage = location.pathname === '/signin';
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [isSidebarMobileOpen, setIsSidebarMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setIsSidebarMobileOpen(!isSidebarMobileOpen);
  };

  return (
    <>
      <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: isSignInPage ? 'transparent' : 'background.default' }}>
        {(!isSignInPage && isAuthenticated) && (
          <Sidebar
            mobileOpen={isSidebarMobileOpen}
            onClose={() => setIsSidebarMobileOpen(false)}
          />
        )}

        <Box sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          width: '100%',
        }}>
          {(!isSignInPage && isAuthenticated) && (
            <Navbar onToggle={handleDrawerToggle} />
          )}

          <Box
            component="main"
            sx={{
              // Clear the fixed AppBar, including the safe-area inset it absorbs
              // at the top on notched PWAs (matches Navbar's height calc).
              mt: (isSignInPage || !isAuthenticated)
                ? 0
                : {
                    xs: 'calc(72px + env(safe-area-inset-top, 0px))',
                    md: 'calc(88px + env(safe-area-inset-top, 0px))',
                  },
              mb: (isSignInPage || !isAuthenticated)
                ? 0
                : {
                    xs: 'calc(76px + env(safe-area-inset-bottom, 0px))',
                    md: 5,
                  },
              px: (isSignInPage || !isAuthenticated) ? 0 : { xs: 2, sm: 3, lg: 4 },
              width: '100%',
              flexGrow: 1,
              display: (isSignInPage || !isAuthenticated) ? 'flex' : 'block',
              flexDirection: 'column',
              minWidth: 0,
              overflowX: 'hidden',
            }}
          >

            <ErrorBoundary resetKey={location.pathname}>
            <Routes>
              <Route path="/signin" element={<SignInView />} />

              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardView />
                </ProtectedRoute>
              } />
              <Route path="/users" element={
                <ProtectedRoute>
                  <UsersView />
                </ProtectedRoute>
              } />
              <Route path="/follow-ups" element={
                <ProtectedRoute>
                  <FollowUpsView />
                </ProtectedRoute>
              } />
              <Route path="/businesses" element={
                <ProtectedRoute>
                  <BusinessesView />
                </ProtectedRoute>
              } />
              <Route path="/products" element={
                <ProtectedRoute>
                  <ProductsView />
                </ProtectedRoute>
              } />
              <Route path="/categories" element={
                <ProtectedRoute>
                  <CategoriesView />
                </ProtectedRoute>
              } />
              <Route path="/sub-categories" element={
                <ProtectedRoute>
                  <SubCategoriesView />
                </ProtectedRoute>
              } />
              <Route path="/brands" element={
                <ProtectedRoute>
                  <BrandsView />
                </ProtectedRoute>
              } />
              <Route path="/plans" element={
                <ProtectedRoute>
                  <PlansView />
                </ProtectedRoute>
              } />
              <Route path="/rfq" element={
                <ProtectedRoute>
                  <RfqView />
                </ProtectedRoute>
              } />
              <Route path="/subscribers" element={
                <ProtectedRoute>
                  <SubscribersView />
                </ProtectedRoute>
              } />
              <Route path="/enquiries" element={
                <ProtectedRoute>
                  <GeneralEnquiriesView />
                </ProtectedRoute>
              } />
              <Route path="/help-requests" element={
                <ProtectedRoute>
                  <HelpRequestsView />
                </ProtectedRoute>
              } />
              <Route path="/buyer-seller-chat" element={
                <ProtectedRoute>
                  <BuyerSellerChatView />
                </ProtectedRoute>
              } />
              <Route path="/chat" element={
                <ProtectedRoute>
                  <BuyerSellerChatView />
                </ProtectedRoute>
              } />
              <Route path="/custom-pages" element={
                <ProtectedRoute>
                  <CustomPagesView />
                </ProtectedRoute>
              } />
              <Route path="/blogs" element={
                <ProtectedRoute>
                  <BlogsView />
                </ProtectedRoute>
              } />
              <Route path="/news-pr" element={
                <ProtectedRoute>
                  <NewsPrView />
                </ProtectedRoute>
              } />
              <Route path="/news" element={
                <ProtectedRoute>
                  <NewsPrView />
                </ProtectedRoute>
              } />
              <Route path="/festivals" element={
                <ProtectedRoute>
                  <FestivalsView />
                </ProtectedRoute>
              } />
              <Route path="/customer-enquiry-data" element={
                <ProtectedRoute>
                  <CustomerEnquiryDataView />
                </ProtectedRoute>
              } />
              <Route path="/countries" element={
                <ProtectedRoute>
                  <CountriesView />
                </ProtectedRoute>
              } />
              <Route path="/states" element={
                <ProtectedRoute>
                  <StatesView />
                </ProtectedRoute>
              } />
              <Route path="/cities" element={
                <ProtectedRoute>
                  <CitiesView />
                </ProtectedRoute>
              } />
              <Route path="/automation" element={
                <ProtectedRoute>
                  <AutomationView />
                </ProtectedRoute>
              } />
              <Route path="/automation-center" element={
                <ProtectedRoute>
                  <AutomationCenterView />
                </ProtectedRoute>
              } />
              <Route path="/analytics" element={
                <ProtectedRoute>
                  <AnalyticsView />
                </ProtectedRoute>
              } />
              <Route path="/marketing-steps" element={
                <ProtectedRoute>
                  <MarketingStepsView />
                </ProtectedRoute>
              } />
              <Route path="/revenue" element={
                <ProtectedRoute>
                  <RevenueView />
                </ProtectedRoute>
              } />
              <Route path="/wbr" element={
                <ProtectedRoute>
                  <WbrReportView />
                </ProtectedRoute>
              } />
              {/* The old Reports page was replaced by the WBR report. */}
              <Route path="/reports" element={<Navigate to="/wbr" replace />} />
              <Route path="/messages" element={
                <ProtectedRoute>
                  <MessagesView />
                </ProtectedRoute>
              } />
              <Route path="/whatsapp" element={
                <ProtectedRoute>
                  <WhatsAppView />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <SettingsView />
                </ProtectedRoute>
              } />
              <Route path="/sop" element={
                <ProtectedRoute>
                  <SopView />
                </ProtectedRoute>
              } />
              <Route path="/partners" element={
                <ProtectedRoute>
                  <PartnersView />
                </ProtectedRoute>
              } />
              <Route path="/partner-payouts" element={
                <ProtectedRoute>
                  <PartnerPayoutsView />
                </ProtectedRoute>
              } />
              <Route path="/partner-settings" element={
                <ProtectedRoute>
                  <PartnerSettingsView />
                </ProtectedRoute>
              } />

              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            </ErrorBoundary>
          </Box>

          {(!isSignInPage && isAuthenticated) && (
            <MobileBottomNav onOpenMenu={handleDrawerToggle} />
          )}
        </Box>
      </Box>
      <PWAInstallPrompt />
    </>
  );
};

export default App;
